// Shared modal for linking a validated insight to a roadmap item.
// Used by Roadmap.tsx (Triage tab) and Review.tsx (Validate & link now).
import { useState, useEffect } from 'react';
import {
  api,
  Insight,
  RoadmapItemWithCounts,
  RoadmapModule,
  RoadmapWorkType,
  RoadmapHorizon,
  RoadmapStatus,
} from '../lib/api';

const MODULE_OPTIONS: { value: RoadmapModule; label: string }[] = [
  { value: 'navigator', label: 'Navigator' },
  { value: 'studio', label: 'Studio' },
  { value: 'quant_engine', label: 'Quant Engine' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'cross_cutting', label: 'Cross-Cutting' },
  { value: 'platform', label: 'Platform' },
];

const WORK_TYPE_OPTIONS: { value: RoadmapWorkType; label: string }[] = [
  { value: 'r_and_d', label: 'R&D' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'product_ux', label: 'Product/UX' },
  { value: 'mixed', label: 'Mixed' },
];

const HORIZON_OPTIONS: { value: RoadmapHorizon; label: string }[] = [
  { value: 'near_term', label: 'Near-Term' },
  { value: 'mid_term', label: 'Mid-Term' },
  { value: 'long_term', label: 'Long-Term' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'deprioritized', label: 'Deprioritized' },
];

function getStatusClass(status: RoadmapStatus): string {
  switch (status) {
    case 'proposed': return 'tag tag-warning';
    case 'active': return 'tag tag-blocker';
    case 'shipped': return 'tag tag-valid';
    case 'deprioritized': return 'tag tag-unclear';
    default: return 'tag';
  }
}

function formatLabel(value: string): string {
  return value.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export interface LinkInsightModalProps {
  insight: Insight;
  currentUserId: string | undefined;
  onLinked: (roadmapItemId: string, roadmapItemTitle: string) => void;
  onClose: () => void;
}

export function LinkInsightModal({ insight, currentUserId, onLinked, onClose }: LinkInsightModalProps) {
  const [tab, setTab] = useState<'existing' | 'new'>('existing');
  const [query, setQuery] = useState('');
  const [roadmapItems, setRoadmapItems] = useState<RoadmapItemWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');
  const [newItem, setNewItem] = useState({
    title: '',
    description: insight.insight_text,
    module_touched: (insight.module_touched === 'none' ? 'cross_cutting' : insight.module_touched) as RoadmapModule,
    work_type: (insight.work_type === 'unclear' ? 'engineering' : insight.work_type) as RoadmapWorkType,
    horizon: 'near_term' as RoadmapHorizon,
  });

  useEffect(() => {
    api.listRoadmapItems({ branch: insight.branch ?? undefined, limit: 100 })
      .then(r => { setRoadmapItems(r.roadmap_items); setLoading(false); })
      .catch(() => setLoading(false));
  }, [insight.branch]);

  const filtered = roadmapItems.filter(i =>
    !query || i.title.toLowerCase().includes(query.toLowerCase())
  );

  const handleLink = async (roadmapItemId: string, title: string) => {
    setLinking(true);
    setError('');
    try {
      await api.linkInsightToRoadmap(roadmapItemId, insight.insight_id, { linked_by: currentUserId });
      onLinked(roadmapItemId, title);
    } catch {
      setError('Failed to link insight');
      setLinking(false);
    }
  };

  const handleCreateAndLink = async () => {
    if (!newItem.title.trim()) return;
    setLinking(true);
    setError('');
    try {
      const created = await api.createRoadmapItem({
        title: newItem.title,
        description: newItem.description || undefined,
        module_touched: newItem.module_touched,
        work_type: newItem.work_type,
        horizon: newItem.horizon,
        status: 'proposed',
      });
      await api.linkInsightToRoadmap(created.roadmap_item_id, insight.insight_id, { linked_by: currentUserId });
      onLinked(created.roadmap_item_id, created.title);
    } catch {
      setError('Failed to create and link');
      setLinking(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Link to Roadmap Item</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
          <div className="text-small text-muted" style={{ marginBottom: '1rem', borderLeft: '2px solid var(--glass-border)', paddingLeft: '0.75rem' }}>
            {insight.insight_text.length > 140 ? insight.insight_text.slice(0, 140) + '...' : insight.insight_text}
          </div>
          <div className="tab-bar" style={{ marginBottom: '1rem' }}>
            <button className={`tab${tab === 'existing' ? ' active' : ''}`} onClick={() => setTab('existing')}>
              Link to existing
            </button>
            <button className={`tab${tab === 'new' ? ' active' : ''}`} onClick={() => setTab('new')}>
              Create new item
            </button>
          </div>
          {tab === 'existing' && (
            <div>
              <input
                type="text"
                className="form-input"
                placeholder="Search roadmap items..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                style={{ marginBottom: '0.75rem' }}
                autoFocus
              />
              {loading && <div className="text-muted" style={{ fontSize: '0.8125rem' }}>Loading...</div>}
              <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {filtered.map(item => (
                  <button key={item.roadmap_item_id} className="card-glass"
                    style={{ textAlign: 'left', cursor: 'pointer', padding: '0.75rem', border: '1px solid var(--glass-border)', width: '100%', background: 'none' }}
                    onClick={() => handleLink(item.roadmap_item_id, item.title)}
                    disabled={linking}>
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{item.title}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                      <span className={getStatusClass(item.status)}>{formatLabel(item.status)}</span>
                      {' '}{item.insight_count} insights
                    </div>
                  </button>
                ))}
                {!loading && filtered.length === 0 && (
                  <div className="text-muted" style={{ fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 0' }}>
                    No items found{query ? ` for "${query}"` : ''}
                  </div>
                )}
              </div>
            </div>
          )}
          {tab === 'new' && (
            <div>
              <div className="form-group">
                <label className="form-label">Title *</label>
                <input type="text" className="form-input" value={newItem.title}
                  onChange={e => setNewItem(p => ({ ...p, title: e.target.value }))}
                  placeholder="Roadmap item title" autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Description (prefilled from insight)</label>
                <textarea className="form-textarea" rows={3} value={newItem.description}
                  onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Module</label>
                  <select className="form-select" value={newItem.module_touched}
                    onChange={e => setNewItem(p => ({ ...p, module_touched: e.target.value as RoadmapModule }))}>
                    {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Work Type</label>
                  <select className="form-select" value={newItem.work_type}
                    onChange={e => setNewItem(p => ({ ...p, work_type: e.target.value as RoadmapWorkType }))}>
                    {WORK_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Horizon</label>
                <select className="form-select" value={newItem.horizon}
                  onChange={e => setNewItem(p => ({ ...p, horizon: e.target.value as RoadmapHorizon }))}>
                  {HORIZON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={linking}>Cancel</button>
          {tab === 'new' && (
            <button className="btn btn-primary" onClick={handleCreateAndLink}
              disabled={!newItem.title.trim() || linking}>
              {linking ? 'Creating...' : 'Create & Link'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
